import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Chip,
  Stack,
  TextField,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Link as LinkIcon,
  AttachFile,
  Close,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
} from '@mui/icons-material';

interface RichTextEditorProps {
  initialContent?: string;
  onSave?: (content: string, files: File[]) => void;
  onChange?: (content: string) => void;
  placeholder?: string;
  minHeight?: number;
  showSaveButton?: boolean;
  saveButtonText?: string;
  disabled?: boolean;
  hideAttachmentButton?: boolean;
}

const MenuBar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor) {
    return null;
  }

  const addLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkInput(false);
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 0.5,
        display: 'flex',
        gap: 0.25,
        alignItems: 'center',
        overflow: 'auto',
      }}
    >
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Underline">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Strikethrough">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          color={editor.isActive('strike') ? 'primary' : 'default'}
        >
          <FormatStrikethrough fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 1">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
          sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
        >
          H1
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 2">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
          sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
        >
          H2
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 3">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
          sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
        >
          H3
        </IconButton>
      </Tooltip>

      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Blockquote">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
        >
          <FormatQuote fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Horizontal Line">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRule fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add Link">
        <IconButton
          size="small"
          onClick={() => {
            if (editor.isActive('link')) {
              removeLink();
            } else {
              setShowLinkInput(!showLinkInput);
            }
          }}
          color={editor.isActive('link') ? 'primary' : 'default'}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {showLinkInput && (
        <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
          <TextField
            size="small"
            placeholder="Enter URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addLink();
              }
              if (e.key === 'Escape') {
                setShowLinkInput(false);
                setLinkUrl('');
              }
            }}
            sx={{ width: 250 }}
          />
          <Button size="small" onClick={addLink}>
            Add
          </Button>
          <Button
            size="small"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
            }}
          >
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent = '',
  onSave,
  onChange,
  placeholder = 'Start typing...',
  minHeight = 120,
  showSaveButton = true,
  saveButtonText = 'Save',
  disabled = false,
  hideAttachmentButton = false,
}) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  // Debounce timer ref for onChange
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced onChange handler
  const debouncedOnChange = useCallback((content: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (onChange) {
        onChange(content);
      }
    }, 150); // 150ms debounce delay
  }, [onChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Create extensions
  const extensions = useMemo(() => [
    StarterKit.configure({
      link: false,
      underline: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'tiptap-link',
      },
    }),
    Placeholder.configure({
      placeholder,
    }),
  ], [placeholder]);

  const editor = useEditor({
    extensions,
    content: initialContent,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const jsonString = JSON.stringify(json);
      debouncedOnChange(jsonString);
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  // Only set content on initial mount, not when initialContent changes
  // This prevents cursor jumping when typing
  useEffect(() => {
    if (editor && initialContent && !editor.isFocused) {
      try {
        const contentObj = JSON.parse(initialContent);
        editor.commands.setContent(contentObj);
      } catch {
        // If not valid JSON, set as plain text
        editor.commands.setContent(initialContent);
      }
    }
  }, [editor]); // Only depend on editor, not initialContent

  // Update placeholder when it changes
  useEffect(() => {
    if (editor) {
      // Force a re-render by updating the view
      editor.view.updateState(editor.state);
    }
  }, [editor, placeholder]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setAttachedFiles([...attachedFiles, ...Array.from(files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(attachedFiles.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (editor && onSave) {
      const json = editor.getJSON();
      const jsonString = JSON.stringify(json);
      onSave(jsonString, attachedFiles);
      // Don't clear content - user wants to keep it
      setAttachedFiles([]);
    }
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <MenuBar editor={editor} />

      <Box
        sx={{
          '& .tiptap-editor': {
            minHeight: `${minHeight}px`,
            p: 2,
            outline: 'none',
            '& p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
            // Paragraphs
            '& p': {
              margin: 0,
              lineHeight: 1.4,
            },
            // Headings
            '& h1': {
              fontSize: '2em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& h2': {
              fontSize: '1.5em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& h3': {
              fontSize: '1.25em',
              fontWeight: 'bold',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            // Lists
            '& ul, & ol': {
              paddingLeft: '1.5em',
              marginTop: '0.5em',
              marginBottom: '0.5em',
            },
            '& li': {
              marginBottom: '0.25em',
            },
            // Blockquote
            '& blockquote': {
              borderLeft: '3px solid',
              borderLeftColor: 'primary.main',
              paddingLeft: '1em',
              marginLeft: 0,
              marginTop: '0.5em',
              marginBottom: '0.5em',
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            // Code block
            '& pre': {
              backgroundColor: 'grey.100',
              borderRadius: '4px',
              padding: '1em',
              marginTop: '0.5em',
              marginBottom: '0.5em',
              overflow: 'auto',
              '& code': {
                fontFamily: 'monospace',
                fontSize: '0.875em',
              },
            },
            // Inline code
            '& code': {
              backgroundColor: 'grey.100',
              borderRadius: '4px',
              padding: '2px 4px',
              fontFamily: 'monospace',
              fontSize: '0.875em',
            },
            // Horizontal rule
            '& hr': {
              border: 'none',
              borderTop: '2px solid',
              borderColor: 'divider',
              marginTop: '1em',
              marginBottom: '1em',
            },
            // Strikethrough
            '& s': {
              textDecoration: 'line-through',
            },
            // Link
            '& .tiptap-link': {
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {attachedFiles.length > 0 && (
        <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {attachedFiles.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => handleRemoveFile(index)}
                size="small"
                deleteIcon={<Close />}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box
        sx={{
          p: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          {!hideAttachmentButton && (
            <>
              <input
                accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                id="rich-text-file-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
              />
              <label htmlFor="rich-text-file-upload">
                <Tooltip title="Attach files">
                  <IconButton component="span" size="small">
                    <AttachFile fontSize="small" />
                  </IconButton>
                </Tooltip>
              </label>
            </>
          )}
        </Box>

        {showSaveButton && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={disabled || !editor}
          >
            {saveButtonText}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default React.memo(RichTextEditor);